<template>
  <section class="dvg">
    <header v-show="!sharedDataStore.isMobile()" class="dvg__head">
      <div class="dvg__head-text">
        <div class="dvg__eyebrow">
          <span class="dvg__dot"></span>
          <span class="brand-mark">WaizmannVergleich</span>&nbsp;/&nbsp;3-TARIFE-VERGLEICH&nbsp;·&nbsp;KRANKENHAUS
        </div>
        <h2 class="dvg__title">Vergleichen Sie 3 Krankenhaus-Tarife</h2>
        <p class="dvg__sub">
          Wählen Sie zwei weitere Tarife, um Leistungen, Wartezeiten und
          Tagegelder direkt gegenüberzustellen.
        </p>
      </div>
    </header>

    <v-alert
      v-if="sharedDataStore.isMobile()"
      type="info"
      variant="tonal"
      density="comfortable"
      rounded="0"
      class="dvg__mobile"
      icon="mdi-phone-rotate-landscape"
    >
      Drehen Sie bitte Ihr Handy, um diesen Vergleich vollständig nutzen zu
      können.
    </v-alert>

    <div class="dvg__scroll">
      <table class="dvg__table">
        <thead>
          <tr>
            <th class="dvg__th dvg__th--label">Tarif</th>
            <th class="dvg__th dvg__th--tarif">
              <div class="dvg__tcol dvg__tcol--fix">
                <span class="dvg__tcol-eyebrow">Tarif&nbsp;1</span>
                <span class="dvg__tcol-name">
                  {{ tarif1.un_name }} {{ tarif1.tarif_title }}
                </span>
                <span class="dvg__tcol-price">
                  {{ tarif1.preis_value }}&nbsp;€
                  <span class="dvg__tcol-unit">mtl.</span>
                </span>
              </div>
            </th>
            <th class="dvg__th dvg__th--tarif">
              <div class="dvg__tcol">
                <span class="dvg__tcol-eyebrow">Tarif&nbsp;2</span>
                <select
                  class="dvg__select"
                  @change="pushTarifToVergleich($event, 2)"
                >
                  <option value selected>Tarif 2 wählen…</option>
                  <option
                    v-for="tarif2 in alletarifeFiltered"
                    :value="tarif2.tarif_id"
                    :key="tarif2.tarif_id"
                  >
                    {{ tarif2.un_name }} {{ tarif2.tarif_title }}
                  </option>
                </select>
                <span v-if="state.tarif2" class="dvg__tcol-price">
                  {{ state.tarif2.preis_value }}&nbsp;€
                  <span class="dvg__tcol-unit">mtl.</span>
                </span>
              </div>
            </th>
            <th class="dvg__th dvg__th--tarif">
              <div class="dvg__tcol">
                <span class="dvg__tcol-eyebrow">Tarif&nbsp;3</span>
                <select
                  class="dvg__select"
                  @change="pushTarifToVergleich($event, 3)"
                >
                  <option value selected>Tarif 3 wählen…</option>
                  <option
                    v-for="tarif3 in alletarifeFiltered"
                    :value="tarif3.tarif_id"
                    :key="tarif3.tarif_id"
                  >
                    {{ tarif3.un_name }} {{ tarif3.tarif_title }}
                  </option>
                </select>
                <span v-if="state.tarif3" class="dvg__tcol-price">
                  {{ state.tarif3.preis_value }}&nbsp;€
                  <span class="dvg__tcol-unit">mtl.</span>
                </span>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td class="dvg__lab"><strong>Monatlicher Beitrag</strong></td>
            <td v-for="tarif in tarifs" :key="`pr-${tarif.tarif_id}`">
              <template v-if="tarif">
                <div class="dvg__price">{{ tarif.preis_value }}&nbsp;€</div>
                <div
                  v-if="tarif.empfehlung_type == 3"
                  class="dvg__badge dvg__badge--rec"
                >
                  Empfehlung „beste Leistung"
                </div>
                <div
                  v-if="
                    tarif.empfehlung_type == 1 || tarif.empfehlung_type == 2
                  "
                  class="dvg__badge"
                >
                  Alternativ-Empfehlung
                </div>
              </template>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Beitragsentwicklung</strong>
              <div class="dvg__sublab">voraussichtlich</div>
            </td>
            <td v-for="tarif in tarifs" :key="`bew-${tarif.tarif_id}`">
              <template v-if="tarif">
                nächstes Jahr: <b>{{ tarif.preis_value_plus_1 }}&nbsp;€</b>
                <br />
                in 5 Jahren: <b>{{ tarif.preis_value_plus_5 }}&nbsp;€</b>
                <br />
                in 10 Jahren: <b>{{ tarif.preis_value_plus_10 }}&nbsp;€</b>
              </template>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Infopaket</strong>
              <div class="dvg__sublab">kostenlos</div>
            </td>
            <td v-for="tarif in tarifs" :key="`btn-${tarif.tarif_id}`">
              <template v-if="tarif && tarif.antraglink">
                <v-btn
                  :href="tarif.antraglink"
                  variant="flat"
                  color="primary"
                  rounded="0"
                  density="comfortable"
                  size="default"
                  class="dvg__cta"
                  append-icon="mdi-arrow-right"
                >
                  Antrag &amp; Infopaket
                  <span
                    v-if="tarif.empfehlung_type > 0"
                    class="dvg__cta-tip"
                  >
                    Tipp
                  </span>
                </v-btn>
              </template>
            </td>
          </tr>

          <template
            v-for="(group, gIdx) in featureGroups"
            :key="`grp-${gIdx}`"
          >
            <tr class="dvg__section">
              <td :colspan="showColspan">
                <span class="dvg__section-eyebrow">{{ group.eyebrow }}</span>
                <h3 class="dvg__section-title">{{ group.title }}</h3>
              </td>
            </tr>
            <tr v-for="feature in group.features" :key="feature.id">
              <td class="dvg__lab">
                <strong>{{ feature.title }}</strong>
              </td>
              <td
                v-for="tarif in tarifs"
                :key="tarif.tarif_id + '-' + feature.id"
              >
                <component
                  :is="getCellTag(tarif[feature.value])"
                  :class="getCellClass(tarif[feature.value])"
                >
                  {{ formatFeatureValue(tarif[feature.value]) }}
                </component>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
  import { ref, onMounted, watch, reactive, computed, defineProps } from "vue";
  import { useSharedDataStore } from "@/stores/sharedData";
  const sharedDataStore = useSharedDataStore();
  const props = defineProps(["tarif1", "alletarife", "berechnung"]);

  const alletarifeFiltered = ref([]);
  const showColspan = ref(2);

  const state = reactive({
    tarif2: null,
    tarif3: null,
  });

  function formatFeatureValue(value) {
    if (value === 0) return "Nein";
    if (value === 1) return "Ja";
    return value;
  }
  function getCellTag(value) {
    if (value === 1 || value === 0) return "strong";
    return "span";
  }
  function getCellClass(value) {
    if (value === 1) return "dvg__pos";
    if (value === 0) return "dvg__neg";
    return "";
  }

  const featureGroups = ref([
    {
      eyebrow: "Bewertung",
      title: "WaizmannWert &amp; Wartezeit",
      features: [
        { id: 1, title: "KH-WaizmannWert", value: "kh_ww" },
        { id: 2, title: "Wartezeit", value: "kh_wz" },
      ],
    },
    {
      eyebrow: "Stationäre Leistung",
      title: "Unterbringung &amp; Arztwahl",
      features: [
        { id: 3, title: "1/2-Bett-Zimmer", value: "kh_bett" },
        { id: 4, title: "Freie Krankenhauswahl", value: "kh_fkhwahl" },
        { id: 11, title: "Leistung in Privatklinik", value: "kh_privatklinik" },
      ],
    },
    {
      eyebrow: "Tagegeld",
      title: "Verzichts-Leistungen",
      features: [
        {
          id: 5,
          title: "Tagegeld bei Verzicht auf 1/2-Bett-Zimmer",
          value: "kh_tgverzbett",
        },
        {
          id: 6,
          title: "Tagegeld bei Verzicht auf Chefarztbehandlung",
          value: "kh_tgverzca",
        },
      ],
    },
    {
      eyebrow: "Behandlung",
      title: "Operationen, Vor- &amp; Nachsorge",
      features: [
        { id: 7, title: "Leistung bei ambulanten OPs", value: "kh_ambops" },
        {
          id: 8,
          title: "Leistung bei Vor- und Nachbehandlungen",
          value: "kh_vornachbeh",
        },
        {
          id: 9,
          title: "Leistung bis zu einem bestimmten Höchstsatz",
          value: "kh_hoechstsatz",
        },
        { id: 10, title: "Leistung bei Kur oder Reha", value: "kh_kurreha" },
      ],
    },
    {
      eyebrow: "Geltungsbereich",
      title: "Europa, weltweit &amp; Transport",
      features: [
        { id: 12, title: "Leistung in Europa", value: "kh_europa" },
        { id: 13, title: "Leistung weltweit", value: "kh_welt" },
        {
          id: 14,
          title: "Leistung für Mehrkosten bei Krankentransport",
          value: "kh_ktransport",
        },
        {
          id: 15,
          title: "Leistung für Krankenrücktransport aus Ausland",
          value: "kh_ktransport_ausland",
        },
      ],
    },
    {
      eyebrow: "Service",
      title: "Anmeldung &amp; Abrechnung",
      features: [
        { id: 16, title: "Mitteilungsfrist", value: "kh_frist" },
        { id: 17, title: "Karte zur Anmeldung / Abrechnung", value: "kh_karte" },
      ],
    },
  ]);

  const tarifs = computed(() => {
    let arr = [props.tarif1];
    if (state.tarif2 !== null) arr.push(state.tarif2);
    if (state.tarif3 !== null) arr.push(state.tarif3);
    return arr;
  });

  onMounted(() => {
    filterAlle();
  });

  function filterAlle() {
    alletarifeFiltered.value = props.alletarife.filter(
      (x) => x.tarif_id !== parseInt(props.tarif1.tarif_id)
    );
  }

  function pushTarifToVergleich(event, tarif_nr) {
    const treffer = props.alletarife.filter(
      (x) => parseInt(x.tarif_id) === parseInt(event.target.value)
    );
    if (tarif_nr === 2) state.tarif2 = treffer[0] || null;
    if (tarif_nr === 3) state.tarif3 = treffer[0] || null;
  }

  watch(
    () => tarifs.value.length,
    (newLength) => {
      showColspan.value = newLength + 1;
    },
    { immediate: true }
  );
</script>

<style scoped>
  .dvg {
    padding: 12px 18px 24px;
    max-width: 1280px;
    margin: 0 auto;
  }
  .dvg__head {
    background: #ffffff;
    border: 1px solid #d8dde5;
    border-left: 3px solid #0f4c81;
    border-radius: 3px;
    padding: 12px 16px;
    margin-bottom: 14px;
  }
  .dvg__eyebrow {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.2px;
    color: #5b6473;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  .dvg__dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #0f4c81;
    margin-right: 8px;
  }
  .dvg__title {
    margin: 0;
    font-size: clamp(18px, 2.2vw, 22px);
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
    line-height: 1.2;
  }
  .dvg__sub {
    margin: 6px 0 0;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.5;
  }
  .dvg__mobile {
    margin-bottom: 14px;
  }
  .dvg__scroll {
    width: 100%;
    overflow-x: auto;
    border: 1px solid #e5e9f0;
    border-radius: 3px;
    background: #ffffff;
  }
  .dvg__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    color: #1f2937;
    min-width: 720px;
  }
  .dvg__table th,
  .dvg__table td {
    padding: 10px 12px;
    vertical-align: top;
    border-bottom: 1px solid #eef1f6;
    text-align: left;
  }
  .dvg__table tbody tr:nth-child(even) td {
    background: #fbfcfe;
  }
  .dvg__th {
    background: #f5f7fb;
    border-bottom: 2px solid #0f4c81;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .dvg__th--label {
    width: 220px;
    font-weight: 700;
    color: #0f4c81;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    font-size: 11.5px;
  }
  .dvg__th--tarif {
    width: 33%;
    min-width: 220px;
  }
  .dvg__tcol {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .dvg__tcol-eyebrow {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 1.1px;
    color: #5b6473;
    text-transform: uppercase;
  }
  .dvg__tcol-name {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.25;
  }
  .dvg__tcol-price {
    font-size: 16px;
    font-weight: 800;
    color: #0f4c81;
    letter-spacing: -0.3px;
    font-variant-numeric: tabular-nums;
  }
  .dvg__tcol-unit {
    font-size: 11px;
    color: #6b7280;
    font-weight: 600;
    margin-left: 2px;
  }
  .dvg__tcol--fix {
    border-bottom: 0;
  }
  .dvg__select {
    width: 100%;
    padding: 6px 8px;
    font-size: 13px;
    border: 1px solid #c8d0db;
    border-radius: 3px;
    background: #fff;
    color: #0f172a;
    font-weight: 600;
    outline: none;
  }
  .dvg__select:focus {
    border-color: #0f4c81;
    box-shadow: 0 0 0 2px rgba(15, 76, 129, 0.12);
  }
  .dvg__lab {
    width: 220px;
    background: #ffffff;
    border-right: 1px solid #eef1f6;
  }
  .dvg__lab strong {
    color: #0f172a;
    font-weight: 700;
  }
  .dvg__sublab {
    font-size: 11.5px;
    color: #6b7280;
    margin-top: 2px;
  }
  .dvg__price {
    font-size: 15px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.2px;
    font-variant-numeric: tabular-nums;
  }
  .dvg__badge {
    display: inline-block;
    margin-top: 4px;
    padding: 2px 8px;
    border-radius: 3px;
    background: #f0f5fa;
    border: 1px solid #d8e3ee;
    color: #0f4c81;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .dvg__badge--rec {
    background: #fff5cc;
    border-color: #ffe58f;
    color: #8a6d00;
  }
  .dvg__cta {
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: none;
    color: #fff !important;
  }
  .dvg__cta-tip {
    margin-left: 6px;
    padding: 1px 6px;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 3px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
  }
  .dvg__pos {
    color: #2e7d32;
    font-weight: 700;
  }
  .dvg__neg {
    color: #b91c1c;
    font-weight: 700;
  }
  .dvg__section td {
    background: #f7faff !important;
    border-top: 2px solid #0f4c81;
    border-bottom: 1px solid #d8e3ee;
    padding: 10px 12px !important;
  }
  .dvg__section-eyebrow {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 1.1px;
    color: #0f4c81;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
  .dvg__section-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.1px;
  }

  @media (max-width: 720px) {
    .dvg {
      padding: 8px 10px 18px;
    }
    .dvg__th--label,
    .dvg__lab {
      width: 160px;
      min-width: 160px;
    }
  }
</style>
